
/* === cart-api.js === */
class CartAPI {

  static async get() {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to get cart: ${response.status}`);
    }

    return await response.json();
  }

  static async add(items) {
    if (!Array.isArray(items)) {
      items = [items];
    }

    const beforeEvent = new CustomEvent('cart:before-add', {
      detail: { items },
      cancelable: true
    });
    window.dispatchEvent(beforeEvent);

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items })
    });

    if (!response.ok) {
      const error = await response.json();
      const err = new Error(error.description || `Failed to add to cart: ${response.status}`);

      window.dispatchEvent(new CustomEvent('cart:error', {
        detail: { error: err.message }
      }));

      throw err;
    }

    const data = await response.json();

    window.dispatchEvent(new CustomEvent('cart:added', {
      detail: { items: data.items }
    }));

    return data;
  }

  static async updateItem(key, quantity) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: key,
        quantity: quantity
      })
    });

    if (!response.ok) {
      const error = new Error(`Failed to update cart: ${response.status}`);
      error.status = response.status;
      throw error;
    }

    const cart = await response.json();

    window.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { cart, source: 'updateItem' }
    }));

    return cart;
  }

  static async updateItems(updates) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates })
    });

    if (!response.ok) {
      throw new Error(`Failed to update cart: ${response.status}`);
    }

    const cart = await response.json();

    window.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { cart, source: 'updateItems' }
    }));

    return cart;
  }

  static async clear() {
    const response = await fetch('/cart/clear.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to clear cart: ${response.status}`);
    }

    window.dispatchEvent(new CustomEvent('cart:cleared'));

    return await response.json();
  }

  static async updateNote(note) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ note })
    });

    if (!response.ok) {
      throw new Error(`Failed to update note: ${response.status}`);
    }

    return await response.json();
  }

  static async applyDiscount(code) {
    window.location.href = `/checkout?discount=${encodeURIComponent(code)}`;
  }
}

window.CartAPI = CartAPI;


/* === cart-manager-unified.js === */
class UnifiedCartManager {

  constructor(container, options = {}) {
    this.container = container;
    this.cart = null;
    this.isLoading = false;
    this.isBatchOperation = false;

    this.config = {
      cssClass: options.cssClass || 'cart-item',
      containerClass: options.containerClass || 'cart-items',

      features: {
        quantitySelector: options.quantitySelector !== false,
        removeButton: options.removeButton !== false,
        timer: options.timer || false,
        notes: options.notes || false,
        autoHide: options.autoHide || false,
        priceBreakdown: options.priceBreakdown !== false,
        checkout: options.checkout !== false,
      },

      debounceTime: options.debounceTime || 500,
      autoHideDelay: options.autoHideDelay || 5000,

      onUpdate: options.onUpdate || (() => {}),
      onError: options.onError || (() => {}),
      onEmpty: options.onEmpty || (() => {}),
    };

    this.timers = new Map();
    this.elements = {};

    this.init();
  }

  init() {
    this.cacheElements();
    this.bindEvents();
    this.initFeatures();
    this.refresh();
  }

  cacheElements() {
    if (!this.container) return;

    this.elements = {
      itemsContainer: this.container.querySelector('[data-cart-items]') || this.container,
      totalElement: this.container.querySelector('[data-cart-total]'),
      subtotalElement: this.container.querySelector('[data-cart-subtotal]') || this.container.querySelector('[data-cart-original-subtotal]'),
      savingsElement: this.container.querySelector('[data-cart-savings]'),
      emptyMessage: this.container.querySelector('[data-cart-empty]'),
      checkoutButton: this.container.querySelector('[data-cart-checkout]'),
      noteField: this.container.querySelector('[data-cart-note]'),

      timerElement: this.container.querySelector('[data-timer]'),
      timerCountdown: this.container.querySelector('[data-timer-countdown]'),

      progressBar: this.container.querySelector('[data-progress-bar]'),
      progressFill: this.container.querySelector('[data-progress-fill]'),
      progressMilestones: this.container.querySelector('[data-progress-milestones]'),
      progressMessage: this.container.querySelector('[data-progress-message]'),

      discountsContainer: this.container.querySelector('[data-cart-discounts]'),
    };
  }

  _hasProperty(item, propName, value = 'true') {
    return item.properties?.[propName] === value;
  }

  _isQuickAddItem(item) {
    return this._hasProperty(item, '_quick_add', 'true');
  }

  _isGiftItem(item) {
    return this._hasProperty(item, '_gift_tier', 'true');
  }

  _isBundleItem(item) {
    return !!item.properties?._bundle_discount;
  }

  _getVisibleItems() {
    if (!this.cart?.items) return [];
    return this.cart.items.filter(item => !this._isQuickAddItem(item));
  }

  bindEvents() {
    this.container.addEventListener('click', (e) => this.handleClick(e));
    this.container.addEventListener('change', (e) => this.handleChange(e));
    this.container.addEventListener('input', (e) => this.handleInput(e));

    window.addEventListener('cart:added', () => {
      if (!this.isBatchOperation) this.refresh();
    });
    window.addEventListener('cart:updated', () => {
      if (!this.isBatchOperation) this.refresh();
    });
    window.addEventListener('cart:cleared', () => this.refresh());

    if (this.config.features.autoHide) {
      this.container.addEventListener('click', (e) => {
        if (e.target === this.container) this.hide();
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.hide();
      });
    }
  }

  handleClick(e) {
    if (e.target.closest('[data-remove-item]')) {
      e.preventDefault();
      const button = e.target.closest('[data-remove-item]');
      const key = button.dataset.removeItem;
      this.removeItem(key);
      return;
    }

    if (e.target.closest('[data-quantity-button]')) {
      e.preventDefault();
      const button = e.target.closest('[data-quantity-button]');
      const input = button.parentElement.querySelector('[data-quantity-input]');
      if (!input) return;

      const currentValue = parseInt(input.value) || 1;
      const isPlus = button.dataset.quantityButton === 'plus';
      const newValue = isPlus ? currentValue + 1 : Math.max(1, currentValue - 1);

      input.value = newValue;
      const key = input.dataset.quantityInput;
      this.debouncedUpdateQuantity(key, newValue);
      return;
    }

    if (e.target.closest('[data-cart-checkout]')) {
      e.preventDefault();
      this.checkout();
      return;
    }

    if (e.target.closest('[data-cart-close]')) {
      e.preventDefault();
      this.hide();
      return;
    }
  }

  handleChange(e) {
    if (e.target.matches('[data-quantity-input]')) {
      const input = e.target;
      const key = input.dataset.quantityInput;
      const quantity = Math.max(1, parseInt(input.value) || 1);

      input.value = quantity;
      this.debouncedUpdateQuantity(key, quantity);
      return;
    }

    if (e.target.matches('[data-quick-add-checkbox]')) {
      this.handleQuickAddToggle(e.target);
      return;
    }

    if (e.target.matches('[data-cart-note]') && this.config.features.notes) {
      const note = e.target.value;
      this.debouncedUpdateNote(note);
      return;
    }
  }

  handleInput(e) {
    if (e.target.matches('[data-quantity-input]')) {
      const input = e.target;
      let value = input.value.replace(/[^0-9]/g, '');

      if (value === '' || parseInt(value) < 1) {
        value = '1';
      }

      input.value = value;
    }
  }

  debouncedUpdateQuantity(key, quantity) {
    this.debounce(`quantity-${key}`, () => {
      this.updateQuantity(key, quantity);
    }, this.config.debounceTime);
  }

  debouncedUpdateNote(note) {
    this.debounce('note', () => {
      this.updateNote(note);
    }, this.config.debounceTime);
  }

  debounce(key, func, wait) {
    if (this.timers.has(key)) {
      clearTimeout(this.timers.get(key));
    }

    const timer = setTimeout(() => {
      this.timers.delete(key);
      func();
    }, wait);

    this.timers.set(key, timer);
  }

  initFeatures() {
    if (this.config.features.timer) {
      this.initTimer();
    }

    if (this.config.features.autoHide) {
      this.initAutoHide();
    }
  }

  async refresh() {
    try {
      this.cart = await window.CartAPI.get();

      if (this.cart.items && this.cart.items.length > 0) {
        await this.fetchCompareAtPrices();
      }

      this.render();
      this.config.onUpdate(this.cart);
    } catch {
      this.config.onError('Failed to load cart. Please refresh the page.');
    }
  }

  async fetchCompareAtPrices() {
    const productHandles = new Map();

    this.cart.items.forEach(item => {
      const urlParts = item.url.split('?')[0].split('/');
      const productHandle = urlParts[urlParts.length - 1];

      if (!productHandles.has(productHandle)) {
        productHandles.set(productHandle, []);
      }
      productHandles.get(productHandle).push(item);
    });

    const promises = Array.from(productHandles.entries()).map(async ([handle, items]) => {
      try {
        const response = await fetch(`/products/${handle}.js`);
        const product = await response.json();

        items.forEach(item => {
          const variant = product.variants.find(v => v.id === item.variant_id);
          if (variant && variant.compare_at_price) {
            item.compare_at_price = variant.compare_at_price;
          }
        });
      } catch {
      }
    });

    await Promise.all(promises);
  }

  render() {
    if (!this.cart) return;

    this.renderItems();
    this.renderPriceBreakdown();
    this.updateEmptyState();
    this.updateFeatures();
  }

  renderItems() {
    if (!this.elements.itemsContainer) return;

    if (!this.cart.items || this.cart.items.length === 0) {
      this.elements.itemsContainer.innerHTML = '';
      return;
    }

    const visibleItems = this._getVisibleItems();

    if (visibleItems.length === 0) {
      this.elements.itemsContainer.innerHTML = '';
      return;
    }

    const itemsHTML = visibleItems.map((item) => {
      return this.renderSingleItem(item);
    }).join('');

    this.elements.itemsContainer.innerHTML = itemsHTML;
  }

  renderSingleItem(item) {
    const hasComparePrice = item.compare_at_price && item.compare_at_price > item.final_price;
    const hasDiscount = item.original_line_price !== item.final_line_price;

    return `
    <div class="${this.config.cssClass}" data-cart-item="${item.key}">
      <!-- Item Image -->
      <div class="cart-item__image">
        <a href="${window.escapeHtml(item.url)}">
          ${item.image ? `<img src="${window.escapeHtml(item.image)}" alt="${window.escapeHtml(item.product_title)}" loading="lazy" width="100" height="100">` : ''}
        </a>
      </div>

      <!-- Item Details -->
      <div class="cart-item__details flex-col">
        <h3 class="cart-item__title">
          <a href="${window.escapeHtml(item.url)}">${window.escapeHtml(item.product_title)}</a>
        </h3>

        ${item.variant_title && item.variant_title !== 'Default Title' ? `
          <p class="cart-item__variant">${window.escapeHtml(item.variant_title)}</p>
        ` : ''}

        <div class="cart-item__price flex-align gap-sm">
          ${this.renderItemPrice(item, hasComparePrice, hasDiscount)}
        </div>

        ${this.renderItemProperties(item)}
        ${this.renderItemDiscounts(item)}

        <!-- Quantity Controls -->
        ${this.config.features.quantitySelector ? `
          <div class="cart-item__quantity flex-align">
            <div class="quantity-selector">
              <button data-quantity-button="minus" aria-label="Decrease quantity">−</button>
              <input
                type="number"
                value="${item.quantity}"
                min="1"
                data-quantity-input="${item.key}"
                aria-label="Quantity"
              >
              <button data-quantity-button="plus" aria-label="Increase quantity">+</button>
            </div>

            ${this.config.features.removeButton ? `
              <button
                class="cart-item__remove flex-center"
                data-remove-item="${item.key}"
                aria-label="Remove ${window.escapeHtml(item.product_title)}"
                title="Remove item"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            ` : ''}
          </div>
        ` : `
          <div class="cart-item__quantity-text">Qty: ${item.quantity}</div>
        `}
      </div>

      <!-- Total Price -->
      <div class="cart-item__actions flex-col">
        <div class="cart-item__total">
          ${hasComparePrice ? `
            <s class="cart-item__price--compare">
              ${window.Currency.formatMoney(item.compare_at_price * item.quantity)}
            </s><br>
          ` : ''}
          ${window.Currency.formatMoney(item.final_line_price)}
        </div>
      </div>
    </div>
    `;
  }

  renderItemPrice(item, hasComparePrice, hasDiscount) {
    if (hasComparePrice) {
      return `
        <s class="cart-item__price--compare">${window.Currency.formatMoney(item.compare_at_price)}</s>
        ${window.Currency.formatMoney(item.final_price)}
      `;
    } else if (hasDiscount) {
      return `
        <s class="cart-item__price--compare">${window.Currency.formatMoney(item.original_price)}</s>
        ${window.Currency.formatMoney(item.final_price)}
      `;
    } else {
      return window.Currency.formatMoney(item.original_price);
    }
  }

  renderItemProperties(item) {
    if (!item.properties || Object.keys(item.properties).length === 0) {
      return '';
    }

    const propertiesHTML = Object.entries(item.properties).map(([key, value]) => {
      if (value && key.charAt(0) !== '_') {
        return `
          <div class="cart-item__property flex gap-sm">
            <span class="cart-item__property-name">${window.escapeHtml(key)}:</span>
            <span class="cart-item__property-value">${window.escapeHtml(value)}</span>
          </div>
        `;
      }
      return '';
    }).join('');

    return propertiesHTML ? `<div class="cart-item__properties flex-col">${propertiesHTML}</div>` : '';
  }

  renderItemDiscounts(item) {
    if (!item.discounts || item.discounts.length === 0) {
      return '';
    }

    const cartLevelTitles = (this.cart?.cart_level_discount_applications || [])
      .map(d => d.title.toLowerCase());

    const itemOnlyDiscounts = item.discounts.filter(
      discount => !cartLevelTitles.includes(discount.title.toLowerCase())
    );

    if (itemOnlyDiscounts.length === 0) {
      return '';
    }

    const discountsHTML = itemOnlyDiscounts.map(discount => `
      <div class="cart-item__discount flex-align gap-sm">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        ${window.escapeHtml(discount.title)}
      </div>
    `).join('');

    return `<div class="cart-item__discounts flex-col">${discountsHTML}</div>`;
  }

  renderPriceBreakdown() {
    if (!this.config.features.priceBreakdown || !this.cart) return;

    let compareSubtotal = 0;
    let hasSavings = false;

    this.cart.items.forEach(item => {
      if (item.compare_at_price && item.compare_at_price > item.final_price) {
        compareSubtotal += item.compare_at_price * item.quantity;
        hasSavings = true;
      } else {
        compareSubtotal += item.original_line_price || (item.original_price * item.quantity);
      }
    });

    const totalSavings = compareSubtotal - this.cart.total_price;
    const showSavings = hasSavings || totalSavings > 0;

    if (this.elements.subtotalElement) {
      const subtotalLine = this.elements.subtotalElement.closest('.cart-price-line');
      if (showSavings) {
        this.elements.subtotalElement.textContent = window.Currency.formatMoney(compareSubtotal);
        subtotalLine?.style.setProperty('display', '');
      } else {
        subtotalLine?.style.setProperty('display', 'none');
      }
    }

    const savingsBlock = this.container.querySelector('[data-cart-savings-block]');
    if (savingsBlock) {
      if (totalSavings > 0) {
        savingsBlock.style.display = '';
        if (this.elements.savingsElement) {
          this.elements.savingsElement.textContent = `-${window.Currency.formatMoney(totalSavings)}`;
        }
      } else {
        savingsBlock.style.display = 'none';
      }
    }

    if (this.elements.totalElement) {
      this.elements.totalElement.textContent = window.Currency.formatMoney(this.cart.total_price);
    }

    this.renderDiscountCodes();
  }

  renderDiscountCodes() {
    const savingsBlock = this.container.querySelector('[data-cart-savings-block]');
    let container = this.elements.discountsContainer;

    if (!this.cart.cart_level_discount_applications || this.cart.cart_level_discount_applications.length === 0) {
      if (container) {
        container.remove();
        this.elements.discountsContainer = null;
      }
      return;
    }

    if (!container && savingsBlock) {
      container = document.createElement('div');
      container.className = 'cart-discounts';
      container.setAttribute('data-cart-discounts', '');
      savingsBlock.appendChild(container);
      this.elements.discountsContainer = container;
    }

    if (!container) return;

    const discountsHTML = this.cart.cart_level_discount_applications.map(discount => `
      <div class="cart-discount flex-between">
        <span class="cart-discount__label flex flex-align gap-xs">
          <svg class="icon icon-discount" width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M3.5 2.5L2 4v4.586a1 1 0 0 0 .293.707l6.414 6.414a1 1 0 0 0 1.414 0l4.586-4.586a1 1 0 0 0 0-1.414L8.293 3.293A1 1 0 0 0 7.586 3H4a1.5 1.5 0 0 0-1.5 1.5z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="5.5" cy="5.5" r="1" fill="currentColor"/>
          </svg>
          <span class="cart-discount__code">${window.escapeHtml(discount.title)}</span>
        </span>
        <span class="cart-discount__right flex flex-align gap-xs">
          <span class="cart-discount__amount">-${window.Currency.formatMoney(discount.total_allocated_amount)}</span>
          <button
            type="button"
            class="cart-discount__remove"
            data-remove-discount="${window.escapeHtml(discount.title)}"
            aria-label="Remove ${window.escapeHtml(discount.title)}"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </span>
      </div>
    `).join('');

    container.innerHTML = discountsHTML;
  }

  updateEmptyState() {
    const visibleItems = this._getVisibleItems();
    const isEmpty = visibleItems.length === 0;

    if (this.elements.emptyMessage) {
      this.elements.emptyMessage.style.display = isEmpty ? 'block' : 'none';
    }

    if (this.elements.itemsContainer && this.elements.itemsContainer !== this.container) {
      this.elements.itemsContainer.style.display = isEmpty ? 'none' : 'block';
    }

    if (this.elements.checkoutButton) {
      this.elements.checkoutButton.style.display = isEmpty ? 'none' : 'block';
    }

    this.container.classList.toggle('cart--empty', isEmpty);
    this.container.classList.toggle('cart-drawer--empty', isEmpty);

    const cartCountElements = document.querySelectorAll('[data-cart-count]');
    cartCountElements.forEach(element => {
      element.textContent = this.cart.item_count || 0;
    });

    if (isEmpty) {
      this.config.onEmpty();
    }
  }

  updateFeatures() {
    if (this.config.features.timer && this.timerEndTime) {
      const remaining = Math.max(0, this.timerEndTime - Date.now());
      this.updateTimer(remaining);
    }

    if (this.elements.progressBar) {
      this.updateProgressBar();
    }
  }

  
  async updateQuantity(key, quantity) {
    const cartItem = this.cart?.items?.find(item => item.key === key);
    const oldQuantity = cartItem ? cartItem.quantity : quantity;
    const input = this.container.querySelector(`[data-quantity-input="${key}"]`);
    const isBundleUpdate = cartItem && this._isBundleItem(cartItem) && quantity > 0;

    try {
      this.setItemLoading(key, true);

      if (isBundleUpdate) {
        this.isBatchOperation = true;

        const variantId = cartItem.variant_id;
        const properties = { ...cartItem.properties };

        await window.CartAPI.updateItem(key, 0);

        await window.CartAPI.add({
          id: variantId,
          quantity: quantity,
          properties: properties
        });

        this.isBatchOperation = false;
      } else {
        await window.CartAPI.updateItem(key, quantity);
      }

      await this.refresh();
    } catch (error) {
      this.isBatchOperation = false;

      if (input) {
        input.value = oldQuantity;
      }

      const errorMessage = error.message?.includes('422')
        ? (window.cartStrings?.stockError || 'Not enough stock available')
        : (window.cartStrings?.updateError || 'Failed to update quantity');

      this.showError(errorMessage);
    } finally {
      this.setItemLoading(key, false);
    }
  }

  async removeItem(key) {
    try {
      this.setItemLoading(key, true);
      await window.CartAPI.updateItem(key, 0);
      await this.refresh();
    } catch {
      this.config.onError('Failed to remove item');
    } finally {
      this.setItemLoading(key, false);
    }
  }

  async handleQuickAddToggle(checkbox) {
    const variantId = parseInt(checkbox.dataset.variantId);
    const isChecked = checkbox.checked;
    const label = checkbox.closest('.cart-quick-add__item');

    if (label) {
      label.classList.add('cart-quick-add__item--loading');
    }

    try {
      if (isChecked) {
        await window.CartAPI.add({
          id: variantId,
          quantity: 1,
          properties: { _quick_add: 'true' }
        });
        if (label) {
          label.classList.add('cart-quick-add__item--checked');
        }
      } else {
        const cartData = await window.CartAPI.get();
        const item = cartData.items.find(i =>
          i.variant_id === variantId &&
          i.properties?._quick_add === 'true'
        );
        if (item) {
          await window.CartAPI.updateItem(item.key, 0);
        }
        if (label) {
          label.classList.remove('cart-quick-add__item--checked');
        }
      }
      this.cart = await window.CartAPI.get();
      this.updateCartCount();
    } catch {
      checkbox.checked = !isChecked;
      if (label) {
        label.classList.toggle('cart-quick-add__item--checked', !isChecked);
      }
      this.showError('Failed to update cart');
    } finally {
      if (label) {
        label.classList.remove('cart-quick-add__item--loading');
      }
    }
  }

  updateCartCount() {
    const count = this.cart?.item_count || 0;
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.closest('[data-cart-count-bubble]')?.classList.toggle('hidden', count === 0);
    });
  }

  async updateNote(note) {
    try {
      await window.CartAPI.updateNote(note);
    } catch {
      this.config.onError('Failed to update note');
    }
  }

  async clearCart() {
    try {
      await window.CartAPI.clear();
      await this.refresh();
    } catch {
      this.config.onError('Failed to clear cart');
    }
  }

  
  show() {
    this.container.classList.add('cart--visible');
    this.refresh();

    if (this.config.features.autoHide) {
      this.scheduleAutoHide();
    }
  }

  hide() {
    this.container.classList.remove('cart--visible');
  }

  checkout() {
    window.location.href = '/checkout';
  }

  setItemLoading(key, isLoading) {
    const item = this.container.querySelector(`[data-cart-item="${key}"]`);
    if (item) {
      item.classList.toggle('cart-item--updating', isLoading);

      const interactiveElements = item.querySelectorAll('[data-quantity-input], [data-quantity-button], [data-remove-item]');
      interactiveElements.forEach(el => {
        el.disabled = isLoading;
      });
    }
  }

  
  initTimer() {
    if (!this.elements.timerElement) return;

    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    const duration = parseInt(this.elements.timerElement.dataset.duration || 15) * 60;
    const timerKey = 'cartTimer';

    if (sessionStorage.getItem(timerKey)) {
      this.timerEndTime = parseInt(sessionStorage.getItem(timerKey));
    } else {
      this.timerEndTime = Date.now() + (duration * 1000);
      sessionStorage.setItem(timerKey, this.timerEndTime.toString());
    }

    this.timerInterval = setInterval(() => {
      const remaining = Math.max(0, this.timerEndTime - Date.now());

      if (isNaN(remaining) || isNaN(this.timerEndTime)) {
        return;
      }

      if (remaining <= 0) {
        this.handleTimerExpired(timerKey);
        return;
      }

      this.updateTimer(remaining);
    }, 1000);

    const remaining = Math.max(0, this.timerEndTime - Date.now());
    if (!isNaN(remaining) && !isNaN(this.timerEndTime)) {
      this.updateTimer(remaining);
    }
  }

  handleTimerExpired(timerKey) {
    clearInterval(this.timerInterval);
    sessionStorage.removeItem(timerKey);
  }

  updateTimer(remainingMs) {
    const timerCountdown = this.container.querySelector('[data-timer-countdown]');
    if (!timerCountdown || !remainingMs || isNaN(remainingMs) || remainingMs < 0) return;

    const minutes = Math.floor(remainingMs / 60000);
    const seconds = Math.floor((remainingMs % 60000) / 1000);
    timerCountdown.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  getProgressTotal() {
    if (!this.cart || !this.cart.items) return 0;

    let total = 0;
    for (const item of this.cart.items) {
      if (!this._isGiftItem(item)) {
        total += item.final_line_price;
      }
    }
    return total / 100;
  }

  updateProgressBar() {
    const bar = this.elements.progressBar;
    if (!bar) return;

    const tiers = JSON.parse(bar.dataset.tiers || '[]')
      .sort((a, b) => a.threshold - b.threshold);

    if (tiers.length === 0) return;

    const currentTotal = this.getProgressTotal();
    const maxThreshold = tiers[tiers.length - 1].threshold;
    const percentage = Math.min(100, (currentTotal / maxThreshold) * 100);

    if (this.elements.progressFill) {
      this.elements.progressFill.style.width = `${percentage}%`;
    }

    this.renderProgressMilestones(tiers, currentTotal, maxThreshold);

    this.updateProgressMessage(tiers, currentTotal);

    this.handleGiftTiers(tiers, currentTotal);
  }

  renderProgressMilestones(tiers, currentTotal, maxThreshold) {
    if (!this.elements.progressMilestones) return;

    const icons = {
      truck: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 3h15v13H1z"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>',
      package: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16.5 9.4l-9-5.19"/><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><path d="M3.27 6.96L12 12.01l8.73-5.05"/><path d="M12 22.08V12"/></svg>',
      gift: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13"/><path d="M19 12v7a2 2 0 01-2 2H7a2 2 0 01-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 010-5C9.5 3 12 8 12 8s2.5-5 4.5-5a2.5 2.5 0 010 5"/></svg>',
      star: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>'
    };

    const milestonesHTML = tiers.map((tier) => {
      const position = (tier.threshold / maxThreshold) * 100;
      const reached = currentTotal >= tier.threshold;
      const iconSvg = tier.icon === 'none' ? '' : (icons[tier.icon] || icons.gift);

      return `
        <div class="progress-milestone${reached ? ' progress-milestone--reached' : ''}${tier.icon === 'none' ? ' progress-milestone--no-icon' : ''}"
             style="left: ${position}%"
             data-tier-type="${tier.type}"
             data-threshold="${tier.threshold}">
          <div class="progress-milestone__icon">
            ${iconSvg}
          </div>
        </div>
      `;
    }).join('');

    this.elements.progressMilestones.innerHTML = milestonesHTML;
  }

  updateProgressMessage(tiers, currentTotal) {
    if (!this.elements.progressMessage) return;

    const nextTier = tiers.find(tier => currentTotal < tier.threshold);

    if (nextTier) {
      const remaining = nextTier.threshold - currentTotal;
      const message = (nextTier.message || '').replace('$amount', window.Currency.formatMoney(remaining * 100));
      this.elements.progressMessage.innerHTML = message;
      this.elements.progressMessage.classList.remove('cart-drawer__progress-message--success', 'cart-page__progress-message--success');
    } else {
      const bar = this.elements.progressBar;
      const completeMessage = bar?.dataset.completeMessage || tiers[tiers.length - 1]?.success || '';
      this.elements.progressMessage.innerHTML = completeMessage;
      this.elements.progressMessage.classList.add('cart-drawer__progress-message--success', 'cart-page__progress-message--success');
    }
  }

  async handleGiftTiers(tiers, currentTotal) {
    const giftTiers = tiers.filter(t => t.type === 'gift' && t.product_id);
    if (giftTiers.length === 0) return;

    if (this.isHandlingGifts) return;
    this.isHandlingGifts = true;

    try {
      for (const giftTier of giftTiers) {
        const giftInCart = this.cart.items.find(item =>
          item.variant_id === giftTier.product_id && this._isGiftItem(item)
        );

        if (currentTotal >= giftTier.threshold && !giftInCart) {
          await window.CartAPI.add({
            id: giftTier.product_id,
            quantity: 1,
            properties: { _gift_tier: 'true' }
          });
          await this.refresh();
        } else if (currentTotal < giftTier.threshold && giftInCart) {
          await window.CartAPI.updateItem(giftInCart.key, 0);
          await this.refresh();
        }
      }
    } finally {
      this.isHandlingGifts = false;
    }
  }

  initAutoHide() {
    this.scheduleAutoHide();
  }

  scheduleAutoHide() {
    this.clearAutoHide();
    this.autoHideTimer = setTimeout(() => {
      this.hide();
    }, this.config.autoHideDelay);
  }

  clearAutoHide() {
    if (this.autoHideTimer) {
      clearTimeout(this.autoHideTimer);
      this.autoHideTimer = null;
    }
  }

  showError(message) {
    const errorContainer = this.container.querySelector('[data-cart-error]');
    const errorMessage = this.container.querySelector('.cart-drawer__error-message');

    if (errorContainer && errorMessage) {
      errorMessage.textContent = message;
      errorContainer.style.display = 'block';

      setTimeout(() => {
        errorContainer.style.display = 'none';
      }, 5000);
    } else {
      alert(message);
    }
  }

  destroy() {
    this.timers.forEach(timer => clearTimeout(timer));
    this.timers.clear();

    if (this.autoHideTimer) {
      clearTimeout(this.autoHideTimer);
    }

    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

  }
}

window.UnifiedCartManager = UnifiedCartManager;


/* === coupon.js === */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-coupon-copy-button]');
  if (!btn) return;

  const code = btn.dataset.couponValue;
  const block = btn.closest('[data-coupon-block]');
  const success = block?.querySelector('.product-coupon__success');
  const autoApply = btn.dataset.couponAutoApply !== 'false';

  try {
    await navigator.clipboard.writeText(code);
  } catch {
    const ta = document.createElement('textarea');
    ta.value = code;
    ta.style.cssText = 'position:fixed;left:-9999px';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }

  btn.classList.add('copied');

  if (autoApply && code) {
    try {
      const response = await fetch('/discount/' + encodeURIComponent(code));
      if (response.ok) {
        window.dispatchEvent(new CustomEvent('cart:updated'));
      }
    } catch {
    }
  }

  if (success) {
    success.classList.remove('hidden');
    void success.offsetWidth;
    success.classList.add('show');
    setTimeout(() => {
      success.classList.remove('show');
      setTimeout(() => success.classList.add('hidden'), 300);
    }, 3000);
  }

  setTimeout(() => btn.classList.remove('copied'), 2000);
});


/* === fly-to-cart.js === */
if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', class FlyToCart extends HTMLElement {
    constructor() {
      super();
      this.isAnimating = false;
      this.sourcePosition = null;
      this.productImageSrc = null;
      this.trailParticles = [];
    }

    connectedCallback() {
      this.config = window.flyToCartConfig || {
        enabled: true,
        type: 'dot',
        duration: 1100
      };

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }

      if (!this.config.enabled) return;

      this.boundBeforeAdd = (e) => this.handleBeforeAdd(e);
      this.boundCartAdded = (e) => this.handleCartAdded(e);

      window.addEventListener('cart:before-add', this.boundBeforeAdd);
      window.addEventListener('cart:added', this.boundCartAdded);
    }

    disconnectedCallback() {
      if (this.boundBeforeAdd) {
        window.removeEventListener('cart:before-add', this.boundBeforeAdd);
      }
      if (this.boundCartAdded) {
        window.removeEventListener('cart:added', this.boundCartAdded);
      }
    }

    handleBeforeAdd(event) {
      const button = document.querySelector('[data-add-to-cart-button]');
      if (!button) return;

      const rect = button.getBoundingClientRect();
      this.sourcePosition = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      };

      if (this.config.type === 'image') {
        const productImage = document.querySelector(
          '.product__media-item--active img, ' +
          '.product__media img, ' +
          '[data-product-media] img, ' +
          '.product-single__photo img'
        );
        if (productImage) {
          this.productImageSrc = productImage.currentSrc || productImage.src;
        }
      }
    }

    handleCartAdded(event) {
      if (!this.config.enabled || this.isAnimating || !this.sourcePosition) return;

      const cartIcon = document.querySelector('.header__icon--cart, [data-cart-drawer-toggle]');
      if (!cartIcon) return;

      const cartRect = cartIcon.getBoundingClientRect();
      const targetPosition = {
        x: cartRect.left + cartRect.width / 2,
        y: cartRect.top + cartRect.height / 2
      };

      this.animate(this.sourcePosition, targetPosition, cartIcon);
    }

    animate(start, end, cartIcon) {
      this.isAnimating = true;

      const flyElement = this.createFlyElement();
      document.body.appendChild(flyElement);

      if (this.config.type === 'line') {
        this.createTrailParticles();
      }

      flyElement.style.left = `${start.x}px`;
      flyElement.style.top = `${start.y}px`;
      flyElement.style.transform = 'translate(-50%, -50%)';
      flyElement.style.opacity = '1';

      flyElement.offsetHeight;

      const controlX = (start.x + end.x) / 2;
      const controlY = Math.min(start.y, end.y) - 80;

      const positionHistory = [];
      const historyLength = 8;

      const startTime = performance.now();
      const duration = this.config.duration;

      const animateFrame = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        const t = 1 - Math.pow(1 - progress, 3);

        const x = this.quadraticBezier(start.x, controlX, end.x, t);
        const y = this.quadraticBezier(start.y, controlY, end.y, t);

        flyElement.style.left = `${x}px`;
        flyElement.style.top = `${y}px`;

        if (this.config.type === 'line') {
          positionHistory.unshift({ x, y });
          if (positionHistory.length > historyLength) {
            positionHistory.pop();
          }
          this.updateTrailParticles(positionHistory, progress);
        }

        if (progress > 0.6) {
          const fadeProgress = (progress - 0.6) / 0.4;
          const scale = 1 - fadeProgress * 0.6;
          const opacity = 1 - fadeProgress;
          flyElement.style.transform = `translate(-50%, -50%) scale(${scale})`;
          flyElement.style.opacity = opacity.toString();
        }

        if (progress < 1) {
          requestAnimationFrame(animateFrame);
        } else {
          this.onAnimationComplete(flyElement, cartIcon);
        }
      };

      requestAnimationFrame(animateFrame);
    }

    createFlyElement() {
      const element = document.createElement('div');
      element.className = `fly-to-cart fly-to-cart--${this.config.type}`;

      if (this.config.type === 'image' && this.productImageSrc) {
        const img = document.createElement('img');
        img.src = this.productImageSrc;
        img.alt = '';
        img.loading = 'eager';
        element.appendChild(img);
      }

      return element;
    }

    createTrailParticles() {
      const particleCount = 6;
      this.trailParticles = [];

      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement('div');
        particle.className = 'fly-to-cart__trail';

        const size = 10 - i * 1.2;
        particle.style.width = `${size}px`;
        particle.style.height = `${size}px`;
        particle.style.opacity = '0';

        document.body.appendChild(particle);
        this.trailParticles.push(particle);
      }
    }

    updateTrailParticles(positionHistory, progress) {
      this.trailParticles.forEach((particle, index) => {
        const posIndex = Math.min(index + 1, positionHistory.length - 1);
        const pos = positionHistory[posIndex];

        if (pos) {
          particle.style.left = `${pos.x}px`;
          particle.style.top = `${pos.y}px`;
          particle.style.transform = 'translate(-50%, -50%)';

          const baseOpacity = 0.5 - index * 0.07;
          const fadeMultiplier = progress > 0.7 ? 1 - (progress - 0.7) / 0.3 : 1;
          particle.style.opacity = Math.max(0, baseOpacity * fadeMultiplier).toString();
        }
      });
    }

    quadraticBezier(p0, p1, p2, t) {
      const mt = 1 - t;
      return mt * mt * p0 + 2 * mt * t * p1 + t * t * p2;
    }

    onAnimationComplete(flyElement, cartIcon) {
      flyElement.remove();

      this.trailParticles.forEach(p => p.remove());
      this.trailParticles = [];

      cartIcon.classList.add('fly-to-cart-bounce');
      setTimeout(() => {
        cartIcon.classList.remove('fly-to-cart-bounce');
      }, 400);

      this.isAnimating = false;
      this.sourcePosition = null;
      this.productImageSrc = null;
    }
  });
}

